import {
  getAppBackendBuildFolder,
  getAppBuildFolder,
} from '@wix/cli-app-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { HttpClient } from '@wix/cli-http-client';
import type { BiLogger } from '@wix/cli-telemetry';
import { DevCenterClient } from '@wix/dev-center-client';
import { HostingService } from './hosting-service.js';
import { BackendDeploymentClient } from '../backend-deployment/client.js';
import { getBackendDeploymentFiles } from '../backend-deployment/deploy.js';
import { replacePlaceholder } from './utils.js';

export const API_BASEURL_PLACEHOLDER =
  'http://__API_BASEURL_PLACEHOLDER__.mock';

export const uploadApplication = async (
  appId: string,
  projectFolder: string,
  biLogger: BiLogger,
  httpClient: HttpClient,
  hasBackendComponents: boolean
) => {
  const hosting = new HostingService(appId, biLogger, httpClient);
  const deployment = new BackendDeploymentClient(biLogger, httpClient);
  const devCenterClient = new DevCenterClient(httpClient);

  const buildFolder = getAppBuildFolder(projectFolder);
  const backendBuildFolder = getAppBackendBuildFolder(projectFolder);

  const backendState = hasBackendComponents
    ? await deployment.createBackendDeployment({
        appId,
      })
    : undefined;

  const { appSecret, webhookPublicKey } = await devCenterClient.getAppSecret({
    appId,
  });

  let revertApiBaseUrlReplacement: (() => Promise<void>) | undefined;

  if (backendState?.deploymentId) {
    revertApiBaseUrlReplacement = await replacePlaceholder({
      directory: buildFolder,
      cwd: projectFolder,
      replacements: [[API_BASEURL_PLACEHOLDER, backendState.deploymentUrl]],
    });
  }

  const revertBackendAppSecretReplacement = await replacePlaceholder({
    directory: backendBuildFolder,
    cwd: projectFolder,
    replacements: [
      ['__APP_SECRET__', appSecret],
      ['__WEBHOOK_PUBLIC_KEY__', webhookPublicKey],
    ],
  });

  let uploadAppError: unknown;
  let serveUrlResult: string | undefined;
  try {
    const [{ serveUrl }] = await Promise.all([
      hosting.uploadApplication(buildFolder),
      backendState?.deploymentId
        ? deployment.updateBackendDeployment({
            appId,
            entrypoint: './entry.mjs',
            deploymentId: backendState.deploymentId,
            files: await getBackendDeploymentFiles(projectFolder),
          })
        : Promise.resolve(),
    ]);
    serveUrlResult = serveUrl;
  } catch (e) {
    // Saving the error to throw it after reverting the placeholder replacement.
    uploadAppError = e;
  }

  // Reverting the placeholder replacement here, so user will be able to run "preview" again.
  if (revertApiBaseUrlReplacement) {
    await revertApiBaseUrlReplacement();
  }

  await revertBackendAppSecretReplacement();

  if (uploadAppError || !serveUrlResult) {
    throw new CliError({
      code: CliErrorCode.UploadApplicationFailed(),
      cause: uploadAppError,
    });
  }
  return {
    serveUrl: serveUrlResult,
    backendServerUrl: backendState?.deploymentUrl,
  };
};
